import React, { useEffect, useState } from "react";
import { useI18n } from "@/i18n";
import { ApiError, createSubscription, fetchSubscriptions, updateSubscription, type Subscription } from "@/lib/api";
import { haptic } from "@/lib/telegram";
import type { Product, ProductVariant } from "@/types";
import { MainAction } from "./MainAction";
import { QuantityStepper } from "./QuantityStepper";

const INTERVALS = [2, 3, 4, 6, 8];

/** Product page sheet: "Deliver every N weeks" (reminder + one-tap reorder, no auto-charge). */
export function SubscribeSheet({ product, variant, open, onClose }: { product: Product; variant: ProductVariant; open: boolean; onClose: () => void }) {
  const { t } = useI18n();
  const [weeks, setWeeks] = useState(4);
  const [qty, setQty] = useState(1);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setDone(false);
      setError(null);
    }
  }, [open]);
  if (!open) return null;

  const submit = async () => {
    if (done) return onClose();
    setBusy(true);
    setError(null);
    try {
      await createSubscription({ productId: product.id, variantId: variant.id, quantity: qty, intervalWeeks: weeks });
      haptic("success");
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("common.error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sheet-overlay" onClick={onClose}>
      <div className="sheet" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div className="sheet__handle" />
        {done ? (
          <div style={{ textAlign: "center", padding: "12px 0" }}>
            <div className="sheet__success-icon">✓</div>
            <h2 className="sheet__title">{t("subscription.doneTitle")}</h2>
            <p className="sheet__text">{t("subscription.doneBody", { weeks })}</p>
          </div>
        ) : (
          <>
            <h2 className="sheet__title">{t("subscription.title")}</h2>
            <p className="sheet__text">
              <strong>{product.name}</strong> · {variant.label}
              <br />
              {t("subscription.intro")}
            </p>
            <div className="field" style={{ marginBottom: 8 }}>
              <label>{t("subscription.every")}</label>
            </div>
            <div className="segmented" style={{ marginBottom: 18 }}>
              {INTERVALS.map((w) => (
                <button key={w} className="chip" data-active={w === weeks} onClick={() => setWeeks(w)}>
                  {t("subscription.weeks", { count: w })}
                </button>
              ))}
            </div>
            <div className="field" style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between" }}>
              <label>{t("subscription.quantity")}</label>
              <QuantityStepper quantity={qty} max={10} onChange={(q) => setQty(Math.max(1, q))} />
            </div>
            {error && <div className="form-error-banner">{error}</div>}
          </>
        )}
        <MainAction text={done ? t("inquiry.close") : t("subscription.submit")} onClick={submit} loading={busy} hidden={!open} />
      </div>
    </div>
  );
}

/** Profile: the customer's subscriptions with pause / resume / cancel. */
export function SubscriptionsList() {
  const { t, locale } = useI18n();
  const [subs, setSubs] = useState<Subscription[]>([]);

  const load = () => fetchSubscriptions().then(setSubs).catch(() => setSubs([]));
  useEffect(() => {
    load();
  }, [locale]);
  if (subs.length === 0) return null;

  const act = async (s: Subscription, status: Subscription["status"]) => {
    haptic("light");
    await updateSubscription(s.id, { status }).catch(() => {});
    load();
  };
  const fmt = (iso: string) => new Intl.DateTimeFormat(locale === "en" ? "en-GB" : "ru-RU", { day: "numeric", month: "long" }).format(new Date(iso));

  return (
    <>
      <div className="section-heading" style={{ marginTop: 8 }}>
        <span className="diamond" />
        {t("subscription.listTitle")}
      </div>
      {subs.map((s) => (
        <div key={s.id} className="card" style={{ padding: 14, marginBottom: 10 }}>
          <div style={{ fontFamily: "var(--font-display)", fontSize: 16 }}>
            {s.product.name} × {s.quantity}
          </div>
          <div style={{ fontSize: 12.5, color: "var(--color-ink-soft)", margin: "4px 0 10px" }}>
            {t("subscription.every")} {t("subscription.weeks", { count: s.intervalWeeks })} ·{" "}
            {s.status === "paused" ? t("subscription.paused") : t("subscription.next", { date: fmt(s.nextDate) })}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn btn--ghost" style={{ minHeight: 38, padding: "8px 14px" }} onClick={() => act(s, s.status === "paused" ? "active" : "paused")}>
              {s.status === "paused" ? t("subscription.resume") : t("subscription.pause")}
            </button>
            <button className="btn btn--danger-text" onClick={() => act(s, "cancelled")}>
              {t("subscription.cancel")}
            </button>
          </div>
        </div>
      ))}
    </>
  );
}
